import type { ImportTasksInput, ImportTasksResult, TaskDto, UpdateTaskInput } from '@werft/contracts';
import { keyBetween } from '@werft/domain';
import { formatDue, t, today } from '../../i18n/index.js';
import { nextDue } from './repeat.js';
import { api, ApiError, newOpId as makeOpId } from '../../platform/api.js';
import { drafts } from '../../platform/drafts.js';
import type { OutboxEntry } from '../../platform/outbox.js';
import { removeTask, setMyDayPinned, upsertTask } from '../../platform/realtime.js';
import { keys, queryClient } from '../../platform/queries.js';
import { outbox } from '../../platform/sync.js';
import { toast } from '../../platform/toast.js';
import { offerUndo, type Revert } from '../../platform/undo.js';

/** opId der zuletzt eingereihten Änderung (für „Rückgängig“ vor dem Senden) */
let lastOpId: string | undefined;
const newOpId = () => (lastOpId = makeOpId());

/**
 * Schreibwege für Aufgaben (Paket P3). Jede Änderung geht über den Ausgangskorb (`platform/outbox.ts`):
 * sofort sichtbar als Overlay, dauerhaft gespeichert bis der Server bestätigt, Wiederholung mit derselben opId.
 * Serverantworten und Live-Ereignisse laufen durch dieselbe Einsortierung (`upsertTask`). Bei Ablehnung bleibt die
 * Eingabe erhalten: Titel kommen zurück ins Eingabefeld, Konflikte bieten „Meine Fassung übernehmen“ an.
 */

type Patch = UpdateTaskInput['patch'];

/** Vorläufige Einträge stehen am Ende, untereinander in Eingabe-Reihenfolge – auch über Neuladen hinweg */
let tempSeq = 0;
const tempPosition = () => `￿${Date.now().toString(36).padStart(9, '0')}${String(++tempSeq).padStart(4, '0')}`;

const addDraftKey = (listId: string) => `add.${listId}`;

function tempTask(projectId: string, listId: string, opId: string, title: string, i = 0): TaskDto {
  const now = new Date().toISOString();
  return {
    id: `tmp-${opId}-${i}`,
    projectId,
    // Nummer vergibt der Server; 0 = noch keine (Schlüssel erscheint nach dem Speichern)
    number: 0,
    listId,
    sectionId: null,
    parentId: null,
    // ADR 0172: ohne Position ans Ende der Spalte, bis der Server sie vergibt
    boardPosition: null,
    title,
    dependsOn: [],
    blockers: 0,
    description: null,
    statusId: '',
    completedAt: null,
    startDate: null,
    dueDate: null,
    priority: null,
    assigneeIds: [],
    commentCount: 0,
    attachmentCount: 0,
    labelIds: [],
    fields: {},
    accent: null,
    cover: null,
    milestone: false,
    repeat: null,
    repeatSpawned: false,
    subtaskCount: 0,
    subtaskDone: 0,
    checklistCount: 0,
    checklistDone: 0,
    onOverview: false,
    position: tempPosition(),
    version: 0,
    createdBy: null,
    createdAt: now,
    updatedAt: now,
  };
}

const messageOf = (err: unknown) => (err instanceof ApiError || err instanceof Error ? err.message : t('task.failed'));

/** Abgelehnte Titel zurück ins Eingabefeld ihrer Liste, damit nichts verloren geht */
function giveBackTitles(e: OutboxEntry, err: unknown) {
  const body = e.body as { listId: string; title?: string; titles?: string[] };
  const titles = body.titles ?? (body.title ? [body.title] : []);
  drafts.append((e.meta as { draftKey?: string } | undefined)?.draftKey ?? addDraftKey(body.listId), titles.join(' · '));
  toast(t('sync.createFailed', { message: messageOf(err) }), { kind: 'error', ms: 8000 });
}

/**
 * Gleicher React-Schlüssel für vorläufige und gespeicherte Aufgabe: die Zeile wird nicht neu aufgebaut, wenn die
 * Serverfassung kommt (P5-Messung: das zweite Aufbauen kostete am Handy die meiste Zeit; Fokus bleibt erhalten).
 */
const stableKeys = new Map<string, string>();
export const rowKey = (id: string) => stableKeys.get(id) ?? id;

outbox.register('task.create', {
  done: (e, r) => {
    const tempId = (e.meta as { tempId?: string } | undefined)?.tempId;
    if (tempId) stableKeys.set((r as TaskDto).id, tempId);
    upsertTask(e.projectId, r as TaskDto);
    if ((e.body as { myDay?: boolean }).myDay) setMyDayPinned(r as TaskDto, true);
    // AF09: Liste wächst über die Schwelle → einmalige Strukturhilfe (nachgeladen)
    if (!(r as TaskDto).parentId) void import('../hints/triggers.js').then((m) => m.afterCreate(e.projectId, (r as TaskDto).listId));
  },
  fail: giveBackTitles,
  expired: (e) => giveBackTitles(e, new Error(t('sync.expired'))),
});

outbox.register('task.myday', {
  done: (e) => {
    const m = e.meta as { task: TaskDto; on: boolean };
    setMyDayPinned(m.task, m.on);
    // AF16: Tag und Reihenfolge des Fokus kommen vom Server
    void queryClient.invalidateQueries({ queryKey: ['inbox', 'day'] });
  },
  fail: (_e, err) => {
    toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' });
  },
});

/** Für heute (oder ab `forDay`, z. B. morgen – AF16) vormerken bzw. aus Mein Tag entfernen (persönlich) */
export function setPinned(task: TaskDto, on: boolean, forDay: string | null = null) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'task.myday',
      projectId: task.projectId,
      method: 'PUT',
      url: `/api/tasks/${task.id}/my-day`,
      body: { opId, on, ...(on && forDay ? { forDay } : {}) },
      overlay: { type: 'patch', taskId: task.id, fields: { pinned: on, pinnedFor: on ? forDay : null } as Partial<TaskDto> },
      meta: { task, on },
    })
    .catch(() => {});
}

outbox.register('task.bulk', {
  done: (e, r) => {
    const items = (r as { items: TaskDto[] }).items;
    items.forEach((x) => upsertTask(e.projectId, x));
    if (items[0]) void import('../hints/triggers.js').then((m) => m.afterCreate(e.projectId, items[0]!.listId));
  },
  fail: giveBackTitles,
  expired: (e) => giveBackTitles(e, new Error(t('sync.expired'))),
});

/* ---------- 0.63: mehrere Aufgaben auf einmal (Einfügen mit Rückfrage, Dialog „Aufgaben importieren …“) ---------- */

outbox.register('task.import', {
  done: (e, r) => {
    const res = r as ImportTasksResult;
    res.items.forEach((x) => upsertTask(e.projectId, x));
    // Neue Status/Labels kommen auch als Ereignis; der Snapshot ist so sofort vollständig
    if (res.statuses || res.labels) void queryClient.invalidateQueries({ queryKey: keys.project(e.projectId) });
    if (res.items[0]) void import('../hints/triggers.js').then((m) => m.afterCreate(e.projectId, res.items[0]!.listId));
  },
  fail: (e, err) => {
    // Nichts geht verloren: der Text lässt sich im Dialog erneut prüfen
    const m = e.meta as { text?: string; listId?: string; statusId?: string; sectionId?: string } | undefined;
    toast(t('import.failed', { message: messageOf(err) }), {
      kind: 'error',
      ms: 12_000,
      ...(m?.text ? { action: { label: t('import.reopen'), run: () => void import('../import/store.js').then((x) => x.openTaskImport({ projectId: e.projectId, listId: m.listId, statusId: m.statusId, sectionId: m.sectionId, text: m.text })) } } : {}),
    });
  },
  expired: () => void toast(t('import.failed', { message: t('sync.expired') }), { kind: 'error' }),
});

/**
 * Aufgaben aus einem geprüften Plan anlegen – über den Ausgangskorb (auch offline), sofort als vorläufige Zeilen
 * sichtbar. Rückmeldung mit „Rückgängig“ (archiviert genau diese Aufgaben; nicht für Gäste).
 */
export function importTasks(input: { projectId: string } & Omit<ImportTasksInput, 'opId' | 'useSourceColumns' | 'memberMap'> & Partial<Pick<ImportTasksInput, 'useSourceColumns' | 'memberMap'>>, opts: { listId: string; text?: string; undo: boolean; statusFor?: (key: string) => string | undefined }): Promise<ImportTasksResult> {
  const opId = newOpId();
  const temps = input.plan.tasks.map((x, i) => ({ ...tempTask(input.projectId, opts.listId, opId, x.title, i), statusId: opts.statusFor?.(x.columnKey) ?? input.statusId ?? '' }));
  const { projectId, ...body } = input;
  const done = outbox.enqueue({
    opId,
    kind: 'task.import',
    projectId,
    method: 'POST',
    url: `/api/projects/${projectId}/tasks/import`,
    body: { opId, ...body },
    overlay: { type: 'create', tasks: temps },
    meta: { text: opts.text, listId: input.listId, statusId: input.statusId, sectionId: input.sectionId },
  }) as Promise<ImportTasksResult>;
  const n = input.plan.tasks.length;
  const revert: Revert = async () => {
    const r = await done;
    const u = await api.post<{ archived: number }>(`/api/projects/${projectId}/tasks/import/undo`, { opId: makeOpId(), taskIds: r.items.map((x) => x.id) });
    r.items.forEach((x) => removeTask(projectId, x.id));
    return u.archived === r.items.length ? null : t('import.undoPartial', { n: u.archived });
  };
  offerUndo(n === 1 ? t('import.done1') : t('import.done', { n }), opts.undo ? [{ opId, done, revert }] : [], { ms: 10_000 });
  return done;
}

/** Felder, die eine Änderung berührt (für die Konfliktprüfung) */
function touched(patch: Patch): (keyof TaskDto)[] {
  const map: Record<string, keyof TaskDto> = { completed: 'completedAt' };
  return Object.keys(patch).map((k) => map[k] ?? (k as keyof TaskDto));
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** ADR 0170: abgewiesene Statuswechsel einer Sammelaktion als EINE Meldung bündeln (keine Meldungsflut) */
let blocked: { id: string; title: string; message: string }[] = [];
let blockedTimer = 0;
function blockedNote(x: { id: string; title: string; message: string }) {
  blocked.push(x);
  window.clearTimeout(blockedTimer);
  blockedTimer = window.setTimeout(() => {
    const list = blocked;
    blocked = [];
    const first = list[0]!;
    const open = () => void import('../../app/router.js').then(({ router }) => router.navigate({ to: '.', search: ((prev: Record<string, unknown>) => ({ ...prev, aufgabe: first.id })) as never }));
    toast(list.length === 1 ? t('transition.blocked', { title: first.title, message: first.message }) : t('transition.blockedMany', { n: list.length, message: first.message }), {
      kind: 'error',
      ms: 12_000,
      action: { label: t('transition.open'), run: open },
    });
  }, 300);
}

/**
 * W-R06 (ADR 0173): Hinweisregeln des Ziels nach dem Wechsel an der Handlung zeigen – ohne zu sperren. Gilt für
 * Statuswahl, Haken, Ziehen, Tabelle und Sammelaktion (alle über denselben Ausgangskorb); mehrere bündeln.
 */
let hinted: { title: string; text: string }[] = [];
let hintTimer = 0;
function hintNote(x: { title: string; text: string }) {
  hinted.push(x);
  window.clearTimeout(hintTimer);
  hintTimer = window.setTimeout(() => {
    const list = hinted;
    hinted = [];
    const texts = [...new Set(list.map((h) => h.text))];
    toast(list.length === 1 ? t('transition.hint', { title: list[0]!.title, message: list[0]!.text }) : t('transition.hintMany', { n: list.length, message: texts.join(' ') }), { ms: 10_000 });
  }, 300);
}

/** Antwort eines Statuswechsels übernehmen; der Hinweistext gehört nicht in den Cache */
function takeTask(projectId: string, r: TaskDto) {
  const { transitionHint, ...dto } = r;
  if (transitionHint) hintNote({ title: dto.title, text: transitionHint });
  upsertTask(projectId, dto);
}

outbox.register('task.update', {
  done: (e, r) => takeTask(e.projectId, r as TaskDto),
  fail: (e, err) => {
    const meta = e.meta as { base: TaskDto; rebases: number };
    const body = e.body as { expectedVersion: number; patch: Patch };
    const current = err instanceof ApiError && err.code === 'version_conflict' ? (err.body.current as TaskDto | undefined) : undefined;
    if (current) {
      // Nur andere Felder wurden geändert: auf den neuen Stand setzen und erneut senden (kein Konflikt für die Person)
      const sameFields = touched(body.patch).every((f) => same(current[f], meta.base[f]));
      if (sameFields && meta.rebases < 5) {
        return { opId: newOpId(), body: { ...body, expectedVersion: current.version }, meta: { base: current, rebases: meta.rebases + 1 } };
      }
      upsertTask(e.projectId, current);
      const fields = e.overlay?.type === 'patch' ? e.overlay.fields : {};
      toast(t('sync.conflict', { title: current.title }), {
        kind: 'error',
        ms: 12_000,
        action: { label: t('sync.keepMine'), run: () => void updateTask(current, body.patch, fields).catch(() => {}) },
      });
      return;
    }
    if (err instanceof ApiError && err.status === 404) {
      removeTask(e.projectId, meta.base.id);
      toast(t('task.notFound'), { kind: 'error' });
      return;
    }
    // ADR 0170 (W3): Voraussetzung des Zielstatus fehlt – Ausgangszustand bleibt (Overlay fällt weg), Grund nennen
    // und zur Lösung führen (die Aufgabe öffnen); gilt für Haken, Ziehen, Tabelle, Sammelaktion und Offline-Abgleich
    if (err instanceof ApiError && err.code === 'transition_blocked') {
      blockedNote({ id: meta.base.id, title: meta.base.title, message: err.message });
      return;
    }
    toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' });
  },
  expired: (e) => toast(t('sync.expiredChange', { title: (e.meta as { base: TaskDto }).base.title }), { kind: 'error', ms: 10_000 }),
});

outbox.register('task.move', {
  done: (e, r) => upsertTask(e.projectId, r as TaskDto),
  fail: (e, err) => {
    const meta = e.meta as { rebases: number };
    const current = err instanceof ApiError && err.code === 'version_conflict' ? (err.body.current as TaskDto | undefined) : undefined;
    // Die Absicht „hierhin“ gilt weiter, auch wenn jemand inzwischen etwas anderes an der Aufgabe geändert hat
    if (current && meta.rebases < 5) {
      return { opId: newOpId(), body: { ...(e.body as object), expectedVersion: current.version }, meta: { rebases: meta.rebases + 1 } };
    }
    if (current) upsertTask(e.projectId, current);
    toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' });
  },
});

outbox.register('task.board', {
  done: (e, r) => takeTask(e.projectId, r as TaskDto),
  fail: (e, err) => {
    const meta = e.meta as { base: TaskDto; rebases: number };
    const current = err instanceof ApiError && err.code === 'version_conflict' ? (err.body.current as TaskDto | undefined) : undefined;
    // Die Absicht „hierhin“ gilt weiter, solange niemand den Status inzwischen anders gesetzt hat
    if (current && current.statusId === meta.base.statusId && meta.rebases < 5) {
      return { opId: newOpId(), body: { ...(e.body as object), expectedVersion: current.version }, meta: { base: current, rebases: meta.rebases + 1 } };
    }
    if (current) {
      upsertTask(e.projectId, current);
      toast(t('sync.conflict', { title: current.title }), { kind: 'error', ms: 12_000 });
      return;
    }
    if (err instanceof ApiError && err.status === 404) {
      removeTask(e.projectId, meta.base.id);
      toast(t('task.notFound'), { kind: 'error' });
      return;
    }
    if (err instanceof ApiError && err.code === 'transition_blocked') {
      blockedNote({ id: meta.base.id, title: meta.base.title, message: err.message });
      return;
    }
    toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' });
  },
  expired: (e) => toast(t('sync.expiredChange', { title: (e.meta as { base: TaskDto }).base.title }), { kind: 'error', ms: 10_000 }),
});

outbox.register('task.archive', {
  done: (e) => removeTask(e.projectId, (e.meta as { taskId: string }).taskId),
  fail: (_e, err) => {
    if (err instanceof ApiError && err.status === 404) return; // schon weg – Ziel erreicht
    toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' });
  },
});

outbox.register('task.restore', {
  done: (e, r) => upsertTask(e.projectId, r as TaskDto),
  fail: (_e, err) => {
    toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' });
  },
});

outbox.register('task.dependencies', {
  done: (e, r) => {
    upsertTask(e.projectId, r as TaskDto);
    void queryClient.invalidateQueries({ queryKey: ['p', e.projectId, 'deps'] });
  },
  fail: (e, err) => {
    void queryClient.invalidateQueries({ queryKey: ['p', e.projectId, 'deps'] });
    toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' });
  },
});

outbox.register('task.assignees', {
  done: (e, r) => upsertTask(e.projectId, r as TaskDto),
  fail: (_e, err) => {
    toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' });
  },
});

/**
 * Anlegen. `listId` leer = Standardliste des Projekts (z. B. aus Mein Tag). `myDay` merkt die Aufgabe für die
 * anlegende Person vor und weist sie ihr zu (Paket P7).
 */
export function createTask(
  projectId: string,
  listId: string,
  title: string,
  opts: { afterId?: string; statusId?: string; completed?: boolean; myDay?: { userId: string }; sectionId?: string | null; parentId?: string; dueDate?: string; assigneeIds?: string[]; priority?: number; labelIds?: string[]; draftKey?: string } = {},
): Promise<TaskDto> {
  const opId = newOpId();
  const temp = {
    ...tempTask(projectId, listId, opId, title),
    sectionId: opts.sectionId ?? null,
    parentId: opts.parentId ?? null,
    statusId: opts.statusId ?? '',
    completedAt: opts.completed ? new Date().toISOString() : null,
    dueDate: opts.dueDate ?? null,
    ...(opts.assigneeIds?.length ? { assigneeIds: opts.assigneeIds } : {}),
    ...(opts.priority ? { priority: opts.priority } : {}),
    ...(opts.labelIds?.length ? { labelIds: opts.labelIds } : {}),
    ...(opts.myDay ? { assigneeIds: [opts.myDay.userId], pinned: true } : {}),
  };
  return outbox.enqueue<TaskDto>({
    opId,
    kind: 'task.create',
    projectId,
    method: 'POST',
    url: `/api/projects/${projectId}/tasks`,
    body: {
      opId,
      title,
      ...(listId ? { listId } : {}),
      ...(opts.afterId ? { afterId: opts.afterId } : {}),
      ...(opts.statusId ? { statusId: opts.statusId } : {}),
      ...(opts.sectionId ? { sectionId: opts.sectionId } : {}),
      ...(opts.parentId ? { parentId: opts.parentId } : {}),
      ...(opts.dueDate ? { dueDate: opts.dueDate } : {}),
      ...(opts.assigneeIds?.length ? { assigneeIds: opts.assigneeIds } : {}),
      ...(opts.priority ? { priority: opts.priority } : {}),
      ...(opts.labelIds?.length ? { labelIds: opts.labelIds } : {}),
      ...(opts.myDay ? { myDay: true, assigneeIds: [opts.myDay.userId] } : {}),
    },
    overlay: { type: 'create', tasks: [temp] },
    meta: { tempId: temp.id, ...(opts.draftKey ? { draftKey: opts.draftKey } : {}) },
  });
}

export function createTasks(projectId: string, listId: string, titles: string[], sectionId?: string | null): Promise<{ items: TaskDto[] }> {
  // Mehrzeilen-Einfügen in einen Abschnitt: einzeln anlegen (das Sammel-Anlegen kennt keine Abschnitte)
  if (sectionId) return Promise.all(titles.map((x) => createTask(projectId, listId, x, { sectionId }))).then((items) => ({ items }));
  const opId = newOpId();
  return outbox.enqueue({
    opId,
    kind: 'task.bulk',
    projectId,
    method: 'POST',
    url: `/api/projects/${projectId}/tasks/bulk`,
    body: { opId, titles, listId },
    overlay: { type: 'create', tasks: titles.map((x, i) => tempTask(projectId, listId, opId, x, i)) },
  });
}

/**
 * Ändern mit Versionsprüfung. `fields` ist die sofort sichtbare Wirkung. Konflikte löst der Handler oben:
 * andere Felder → automatisch neu aufsetzen; dasselbe Feld → Serverstand zeigen, Eingabe per Aktion zurückholbar.
 */
/**
 * Abnahme 0.49 F01: Ausgangsstand einer Änderung = neuester bekannter Serverstand (Details-Cache, versionsbewusst)
 * plus noch offene eigene Änderungen derselben Aufgabe („fortgeschrieben“). Sonst prallt eine schnelle Folgeänderung
 * aus einer Ansicht mit älterem Objekt (Kalender, Zeitachse) an der eigenen vorherigen ab. Echte fremde Änderungen
 * bleiben geschützt: deren Felder stehen nicht in den eigenen offenen Änderungen.
 */
export function projectedBase(base: TaskDto): TaskDto {
  const cached = queryClient.getQueryData<TaskDto>(keys.task(base.projectId, base.id));
  let out = cached && cached.version > base.version ? cached : base;
  for (const e of outbox.entries()) {
    if (e.projectId !== base.projectId || e.overlay?.type !== 'patch' || e.overlay.taskId !== base.id) continue;
    out = { ...out, ...e.overlay.fields };
  }
  return out;
}

export function updateTask(given: TaskDto, patch: Patch, fields: Partial<TaskDto>): Promise<TaskDto> {
  const base = projectedBase(given);
  const opId = newOpId();
  return outbox.enqueue<TaskDto>({
    opId,
    kind: 'task.update',
    projectId: base.projectId,
    method: 'PATCH',
    url: `/api/tasks/${base.id}`,
    body: { opId, expectedVersion: base.version, patch },
    overlay: { type: 'patch', taskId: base.id, fields },
    meta: { base, rebases: 0 },
  });
}

export function setCompleted(task: TaskDto, completed: boolean): Promise<unknown> {
  // AF12: eine erledigte Wiederholung hat ggf. schon ihre Nachfolgerin – Wiederöffnen berücksichtigt beide
  if (!completed && task.completedAt && task.repeatSpawned) {
    return reopenTask(task).then(
      (note) => note && toast(note, { ms: 10_000 }),
      (err) => toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' }),
    );
  }
  const done = updateTask(task, { completed }, { completedAt: completed ? new Date().toISOString() : null });
  const opId = lastOpId;
  // P19: die nächste Wiederholung legt der Server an – ankündigen, wann sie fällig ist, mit geprüftem Rückweg
  if (completed && !task.completedAt && task.repeat && !task.repeatSpawned) {
    offerUndo(t('repeat.next', { date: formatDue(nextDue(task, today())) }), [{ opId, done, revert: () => reopenTask(task) }]);
  }
  // AF09: Vorschläge nach dem eigenen Erledigen (wartende Aufgabe kann starten, alle Schritte fertig)
  if (completed && !task.completedAt) done.then((r) => r && void import('../hints/triggers.js').then((m) => m.afterComplete(task)), () => {});
  if (completed && !task.completedAt && task.milestone && !task.repeat) {
    // AF13: ein erreichter Meilenstein bekommt einmal eine ruhige besondere Rückmeldung
    done.then(() => toast(t('feedback.milestone', { title: task.title }), { kind: 'success', ms: 6000 }), () => {});
  }
  return done;
}

/**
 * Verschieben innerhalb oder zwischen Listen. Die vorläufige Position rechnet der Browser mit demselben Verfahren
 * wie der Server; die endgültige liefert die Antwort.
 */
export function moveTask(task: TaskDto, listId: string, prev: TaskDto | null, next: TaskDto | null, sectionId?: string | null): Promise<unknown> {
  // Abschnitt: weggelassen = beibehalten (in derselben Liste), beim Listenwechsel ohne Abschnitt
  const section = sectionId !== undefined ? sectionId : listId === task.listId ? task.sectionId : null;
  let position = task.position;
  try {
    position = keyBetween(prev?.position ?? null, next?.position ?? null);
  } catch {
    /* Kollision: der Server rechnet neu */
  }
  const opId = newOpId();
  return outbox
    .enqueue({
      opId,
      kind: 'task.move',
      projectId: task.projectId,
      method: 'POST',
      url: `/api/tasks/${task.id}/move`,
      body: { opId, expectedVersion: task.version, listId, sectionId: section, ...(prev ? { afterId: prev.id } : next ? { beforeId: next.id } : {}) },
      overlay: { type: 'patch', taskId: task.id, fields: { listId, position, sectionId: section } },
      meta: { rebases: 0 },
    })
    .catch(() => {});
}

/**
 * ADR 0172 (Nachprüfung W-R01–W-R03): Ablegen im Board als EIN Vorgang – Zielstatus und Stelle zwischen `above`
 * (darüber) und `below` (darunter). Vorschau = Ergebnis: Overlay mit derselben Boardposition, die der Server rechnet;
 * wird der Status abgewiesen, fällt das Overlay weg und die Karte steht wieder, wo sie war.
 */
export function boardMove(given: TaskDto, status: { id: string; category: string }, above: TaskDto | null, below: TaskDto | null): Promise<TaskDto> {
  const task = projectedBase(given);
  let boardPosition = task.boardPosition;
  try {
    boardPosition = above || below ? keyBetween(above?.boardPosition ?? null, below?.boardPosition ?? null) : keyBetween(boardPosition, null);
  } catch {
    /* Kollision oder Altbestand: der Server verteilt neu */
  }
  const fields: Partial<TaskDto> = { boardPosition };
  if (status.id !== task.statusId) {
    fields.statusId = status.id;
    fields.completedAt = status.category === 'done' ? (task.completedAt ?? new Date().toISOString()) : null;
  }
  const opId = newOpId();
  return outbox.enqueue<TaskDto>({
    opId,
    kind: 'task.board',
    projectId: task.projectId,
    method: 'POST',
    url: `/api/tasks/${task.id}/board`,
    body: { opId, expectedVersion: task.version, statusId: status.id, afterId: above?.id ?? null, beforeId: below?.id ?? null },
    overlay: { type: 'patch', taskId: task.id, fields },
    meta: { base: task, rebases: 0 },
  });
}

/** Mehrere auf einmal löschen (Sammelaktion, Paket P8): ein Hinweis mit „Rückgängig“ für alle */
export function archiveTasks(tasks: TaskDto[]): void {
  const ops = tasks.map((task) => {
    const opId = newOpId();
    void outbox
      .enqueue({ opId, kind: 'task.archive', projectId: task.projectId, method: 'DELETE', url: `/api/tasks/${task.id}`, body: { opId }, overlay: { type: 'remove', taskId: task.id }, meta: { taskId: task.id } })
      .catch(() => {});
    return { task, opId };
  });
  toast(t('select.deleted', { n: tasks.length }), {
    action: {
      label: t('common.undo'),
      run: () => {
        for (const { task, opId } of ops) {
          if (outbox.cancel(opId)) continue;
          const restoreOp = newOpId();
          void outbox
            .enqueue({ opId: restoreOp, kind: 'task.restore', projectId: task.projectId, method: 'POST', url: `/api/tasks/${task.id}/restore`, body: { opId: restoreOp }, overlay: { type: 'create', tasks: [task] } })
            .catch(() => {});
        }
      },
    },
  });
}

/** Löschen = archivieren. „Rückgängig“ nimmt die Änderung zurück, solange sie noch nicht gesendet ist. */
export function archiveTask(task: TaskDto): void {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'task.archive',
      projectId: task.projectId,
      method: 'DELETE',
      url: `/api/tasks/${task.id}`,
      body: { opId },
      overlay: { type: 'remove', taskId: task.id },
      meta: { taskId: task.id },
    })
    .catch(() => {});
  toast(t('task.deleted', { title: task.title }), {
    action: {
      label: t('common.undo'),
      run: () => {
        if (outbox.cancel(opId)) return;
        const restoreOp = newOpId();
        void outbox
          .enqueue({
            opId: restoreOp,
            kind: 'task.restore',
            projectId: task.projectId,
            method: 'POST',
            url: `/api/tasks/${task.id}/restore`,
            body: { opId: restoreOp },
            overlay: { type: 'create', tasks: [task] },
          })
          .catch(() => {});
      },
    },
  });
}

export function changeAssignees(task: TaskDto, add: string[], remove: string[]): Promise<unknown> {
  const opId = newOpId();
  const assigneeIds = [...task.assigneeIds.filter((x) => !remove.includes(x)), ...add.filter((x) => !task.assigneeIds.includes(x))];
  return outbox
    .enqueue({
      opId,
      kind: 'task.assignees',
      projectId: task.projectId,
      method: 'PATCH',
      url: `/api/tasks/${task.id}/assignees`,
      body: { opId, add, remove },
      overlay: { type: 'patch', taskId: task.id, fields: { assigneeIds } },
    })
    .catch(() => {});
}

/** P26: „wartet auf“ als Delta */
export function changeDependencies(task: TaskDto, add: string[], remove: string[]) {
  const opId = newOpId();
  const dependsOn = [...task.dependsOn.filter((x) => !remove.includes(x)), ...add.filter((x) => !task.dependsOn.includes(x))];
  void outbox
    .enqueue({
      opId,
      kind: 'task.dependencies',
      projectId: task.projectId,
      method: 'PATCH',
      url: `/api/tasks/${task.id}/dependencies`,
      body: { opId, add, remove },
      overlay: { type: 'patch', taskId: task.id, fields: { dependsOn } },
    })
    .catch(() => {});
}

/**
 * Netzfehler: Ausgang unbekannt – mit DERSELBEN opId wiederholen (der Server liefert dann das gespeicherte Ergebnis).
 * Die opId steckt im Aufruf `send`, der bei jeder Wiederholung denselben Body schickt. Fachliche Ablehnungen (4xx)
 * werden nicht wiederholt. (Nur noch für die Beschreibung, die seriell speichert.)
 */
export async function sendWithRetry<T>(send: () => Promise<T>, attempts = 4): Promise<T> {
  for (let i = 1; ; i++) {
    try {
      return await send();
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 0 || i >= attempts) throw err;
      await new Promise((r) => setTimeout(r, 400 * 2 ** i));
    }
  }
}

/* ── Briefing „Arbeitsfreude“ Etappe B: Rückmeldung mit Bedeutung und Rückgängig (AF11/AF12) ─────────────────── */

/** Aktueller Serverstand – ein Rückweg prüft gegen ihn, nicht gegen den Stand beim Klick */
export const fresh = (task: TaskDto) => api.get<TaskDto>(`/api/tasks/${task.id}`);

/** opId der zuletzt eingereihten Änderung (für „Rückgängig“ vor dem Senden) */
export const lastOp = () => lastOpId;

/**
 * Wieder öffnen als geprüfte Gegenoperation (Server `POST /tasks/:id/reopen`): eine durch das Erledigen entstandene,
 * noch unberührte Wiederholung verschwindet mit; eine schon bearbeitete bleibt und wird nie still gelöscht.
 */
export async function reopenTask(task: TaskDto): Promise<string | null> {
  const now = await fresh(task);
  if (!now.completedAt) return null;
  const r = await api.post<{ task: TaskDto; successor: 'removed' | 'kept' | null }>(`/api/tasks/${task.id}/reopen`, { opId: newOpId(), expectedVersion: now.version });
  upsertTask(task.projectId, r.task);
  return r.successor === 'kept' ? t('undo.successorKept', { title: now.title }) : null;
}

